import Component from 'core/Component';
import { ajax } from 'toolbox/ajax';
import { Event } from 'services/EventEmitter';
import { trigger } from 'toolbox/event';
import { deepMerge } from 'toolbox/deepMerge';
import { mobileScrollEnable, mobileScrollDisable, scrollTo } from 'toolbox/scroll';
import { stringify, parse, appendUrl } from 'toolbox/queryString';
import { Seo } from 'services/Seo';
import Accessibility from 'mixins/Accessibility';
import { mix } from 'core/mixwith';

/**
 * This is a description of the Search constructor function.
 * @class
 * @classdesc This is a description of the Search class.
 * @extends Component
 */
export default class Search extends mix(Component).with(Accessibility) {
    /**
     * Constructor of the class that mainly merge the options of the components
     *
     * @param {HTMLElement} element HTMLElement of the component
     * @param {Object} options That belongs to the component
     * @param {Object} store Store
     */
    constructor(element, options = {}, store) {
        super(element, deepMerge({
            scrollTopOnUpdate: true, // Scroll to top after the result is refreshed.
            scrollToElementOnUpdate: null, // scrollTopOnUpdate needs to be enabled Scroll to element on update.
            scrollTopOffset: 153, // Change the edge offset If you have a fixed navigation bar
            updateUrl: true, // Update the url
            productPageSize: null,
            metaTitle: '',
            metaDescription: '',
            paginatedMetaTitle: '',
            paginatedMetaDescription: '',
            urlSearchParams: null, // object of params that will be sent with search request
            a11y: {
                ariaLiveOpened: '', // Text used for the voice message when refinements panel is opening
                ariaLiveClosed: '', // Text used for the voice message when refinements panel is closing
            },
            ariaCloseTimeout: 5000,
        }, options), store);
    }

    /**
     * All selectors must be cached. Never cache elements that are out of the component scope
     */
    initCache() {
        this.selectors.refinementsAriaLive = document.querySelector('[data-js-refinements-aria]');
        this.selectors.refinementsPanel = document.querySelector('[data-js-search-refinements-panel]');
    }

    /**
     * Init the different state of the component
     * It helps to avoid heavy DOM manipulation
     */
    initState() {
        this.state.insertMode = false;
        this.state.refinementsPanelOpened = false;
        this.state.currentRefinementUrl = window.location.href;

        // Store the initial history state so we can retrieve it later on event 'state.changed'
        // We can later load this state when the user browses back to this point
        this.updateUrl({
            url: this.state.currentRefinementUrl,
            insertMode: false,
            replaceState: true,
        });
    }

    /**
     * Should contain only event listeners and nothing else
     * All the event handlers should be into a separated function. No usage of anonymous function
     */
    bindEvents() {
        Event.on('search.paging.update', this.updateSearch, this);
        Event.on('search.sort.update', this.updateSearch, this);
        Event.on('search.refinementsPanel.toggle', this.toggleRefinementsPanel, this);
        Event.on('state.changed', this.updateSearch, this);
        Event.on('search.refinements.apply', this.updateSearch, this);
    }

    /**
     * Toggle Refinements panel.
     * Controls for this functionality are used only for mobile
     */
    toggleRefinementsPanel() {
        if (this.state.refinementsPanelOpened) {
            this.closeRefinementsPanel();
        } else {
            this.openRefinementsPanel();
        }
    }

    /**
     * Close Refinements panel
     */
    closeRefinementsPanel() {
        this.state.refinementsPanelOpened = false;
        this.element.classList.remove('m-refinements-panel-active');
        mobileScrollEnable();

        if (this.selectors.refinementsAriaLive && this.selectors.refinementsPanel) {
            this.updateAria(this.selectors.refinementsAriaLive, this.options.a11y.ariaLiveClosed);
            this.selectors.refinementsPanel.removeAttribute('role');
            this.selectors.refinementsPanel.removeAttribute('aria-modal');
            this.selectors.refinementsPanel.removeAttribute('aria-labelledby');
        }

        this.destroyModalPattern(true);
    }

    /**
     * Open Refinements panel
     */
    openRefinementsPanel() {
        const accordion = document.querySelector('[data-js-refinements-accordion]');
        const expandedItems = accordion && Array.from(accordion.querySelectorAll('.m-expanded'));
        this.state.refinementsPanelOpened = true;
        this.element.classList.add('m-refinements-panel-active');
        if (accordion) {
            trigger('accordion.open', accordion, { expandedItems });
        }
        mobileScrollDisable();

        if (this.selectors.refinementsAriaLive && this.selectors.refinementsPanel) {
            this.updateAria(this.selectors.refinementsAriaLive, this.options.a11y.ariaLiveOpened);
            this.selectors.refinementsPanel.setAttribute('role', 'dialog');
            this.selectors.refinementsPanel.setAttribute('aria-modal', 'true');
            this.selectors.refinementsPanel.setAttribute('aria-labelledby', 'refinements-navigation-menu');
        }

        if (accordion) {
            this.initModalPattern(accordion, null, true);
        }
    }

    /**
     * Trigger an update of the search results
     *
     * @param {Object} params incoming for update search
     * @returns {Promise} Ajax request
     */
    updateSearch(params) {
        const headers = {};
        this.state.insertMode = !!params.insertMode;

        if (this.options.urlSearchParams) {
            params.url = appendUrl(params.url, this.options.urlSearchParams);
        }

        const urlParams = Object.assign({}, params.urlParams);

        if (this.state.insertMode) {
            urlParams.insertMode = true;
        }

        if (urlParams) {
            params.url = appendUrl(params.url, urlParams);
        }

        if (params.refinementId) {
            headers._refinement = params.refinementId;
        }

        Event.emit('search.update', params, this.state.insertMode);

        const url = this.getSearchUrl(params.url);

        return ajax(url, { headers })
            .then(this.onSearchUpdated.bind(this, params))
            .catch(this.onSearchFailed.bind(this));
    }

    /**
     * Refresh the URL
     *
     * @param {Object} params incoming for update search
     */
    updateUrl(params) {
        // there is no need to add new history state if the current search state is retrieved from history
        if (params && !params.isHistoryState && params.url) {
            SystemJS.import('services/History').then((History) => {
                params.isHistoryState = true;
                if (!params.replaceState) {
                    History.pushState(params, '', params.url);
                } else {
                    History.replaceState(params, '', params.url);
                }
            });
        }
    }

    /**
     *  Get the full Url with all the filter params passed as a query string
     *
     * @param {String} url incoming current url
     * @returns {String} url
     */
    getSearchUrl(url) {
        return url || window.location.pathname;
    }

    /**
     * Callback when results of the search has been returned
     *
     * @param {Object} params for search
     * @param {Object} data Data
     */
    onSearchUpdated(params, data) { // eslint-disable-line complexity
        data = this.processUpdateData(data);

        if (data) {
            data.template = this.element.querySelector('[data-component="search/SearchGrid"]');
            Event.emit('search.updated', data, this.state.insertMode);
        }

        params.url = params.url || this.state.currentRefinementUrl;

        if (params.insertMode) {
            params.url = this.rewriteUrlPagingParams(params.url);
        }

        if (!params.dontUpdateURL) {
            // used for mobile, to don't update URL for temporary refinements (til them will be not applied)
            this.state.currentRefinementUrl = params.url;

            if (this.options.updateUrl) {
                const urlParams = parse(window.location.search);

                if ((data
                    && data.refinements
                    && data.refinements.refinementsCount
                    && !data.refinements.isCategorySearch) || urlParams.reftype === 'product') {
                    params.url = appendUrl(params.url, { reftype: 'product' });
                }
                this.updateUrl(params);
            }
        }

        if (data.isCategorySearch) { // for now, only enabled on CLP
            this.updateSeo(params.url);
        }

        if (this.options.scrollTopOnUpdate && !this.state.insertMode) {
            const scrollToElementOnUpdate = this.element.querySelector(this.options.scrollToElementOnUpdate);

            scrollTo(scrollToElementOnUpdate || this.element, this.options.scrollTopOffset);
        }

        this.state.insertMode = false;
    }

    /**
     * Update seo based on  new pagination params
     * @param {string} url -url
     * @returns {boolean} -status true if seo has been updated
     */
    updateSeo(url) {
        const [, QueryStr] = url.split('?');
        const parsedQueryStr = parse(QueryStr);
        const start = parseInt(parsedQueryStr.start, 10);
        const sz = parseInt(parsedQueryStr.sz, 10);
        const pagingIndex = Math.floor((start + sz) / this.options.productPageSize);
        if (this.options.productPageSize === null || Number.isNaN(pagingIndex)) {
            return false;
        }

        const canicalHref = Seo.getCanonicalHref();
        const [canonicalBaseUrl, canonicalQueryStr] = canicalHref && canicalHref.split('?');
        const parsedCanonicalQueryStr = parse(canonicalQueryStr);
        if (pagingIndex > 1) {
            Seo.setTitle(this.options.paginatedMetaTitle.replace('{pagingIndex}', pagingIndex));
            Seo.setDescription(this.options.paginatedMetaDescription.replace('{pagingIndex}', pagingIndex));
            parsedCanonicalQueryStr.sz = sz;
            parsedCanonicalQueryStr.start = start;
        } else {
            Seo.setTitle(this.options.metaTitle);
            Seo.setDescription(this.options.metaDescription);
            delete parsedCanonicalQueryStr.sz;
            delete parsedCanonicalQueryStr.start;
        }

        const newCanonicalQuery = stringify(parsedCanonicalQueryStr, true);
        Seo.setCanonicalHref(newCanonicalQuery ? `${canonicalBaseUrl}?${newCanonicalQuery}` : canonicalBaseUrl);
        Seo.getAlternateLinksElts().forEach((alternateLink) => {
            if (!alternateLink.href) {
                return;
            }
            const [baseUrl] = alternateLink.href.split('?');
            const newAlternateQuery = stringify(parsedCanonicalQueryStr, true);
            alternateLink.href = newAlternateQuery ? `${baseUrl}?${newAlternateQuery}` : baseUrl;
        });
        return true;
    }

    /**
     * Rewrites the url queryString 'start' and 'sz' params for pagingType 'loadMore'
     * @param {String} url - original url, valid for pagingType 'paging'
     * @returns {String} modified url, valid for pagingType 'loadMore'
     */
    rewriteUrlPagingParams(url) {
        const [baseUrl, searchQuery] = url.split('?');
        const parsedQueryString = parse(searchQuery);
        const start = parseInt(parsedQueryString.start, 10);
        const sz = parseInt(parsedQueryString.sz, 10);
        if (Number.isNaN(start + sz)) {
            return url;
        }
        parsedQueryString.start = 0;
        parsedQueryString.sz = start + sz;
        return `${baseUrl}?${stringify(parsedQueryString, true)}`;
    }

    /**
     * Callback when a search failed
     */
    onSearchFailed() {
        Event.emit('search.failed');
        Event.emit('search.removeLoader');
    }


    /**
     * Remove the CQuotient Activity script and page dwMarker from the response
     * @param {Object|String} data response data
     * @returns {Object} parsed data
     */
    processUpdateData(data) {
        // if the environment is not DW Toolkit, return the data as is
        if (!window.loraDataLayer.isDWToolkitEnvironment) {
            return data;
        }

        // remove the CQuotient Activity script from the response
        if (typeof data === 'string' && data.includes('<!-- CQuotient Activity')) {
            // replace all content betweeen <!-- CQuotient Activity and // </script>
            data = data.replace(/<!-- CQuotient Activity[\s\S]*?<\/script>[\s\S]/g, '');
            // replace all content between <!-- dwMarker and // -->
            data = data.replace(/<!-- dwMarker="dw-object-page"[\s\S]*?-->/g, '');
            data = data.replace(/<!-- dwMarker="page"[\s\S]*?-->/g, '');
            data = data.trim();

            try {
                data = JSON.parse(data);
            } catch (e) {
                console.error('Unable to parse the response data after removing CQuotient Activity script and dwMarkers');
                data = {};
            }
        }

        return data;
    }
}
